import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { MyAELogo } from "../components/myae/Logo";
import SocialLinks from "../components/SocialLinks";
import { track } from "../lib/analytics";
import {
  ArrowRight, FileText, Sparkles, ShieldCheck, Target, GraduationCap,
  Upload, BarChart3, CheckCircle2,
} from "lucide-react";

const STEPS = [
  { icon: Upload, title: "Upload your assignment", desc: "Add your brief, marking criteria and current draft in seconds." },
  { icon: Sparkles, title: "MyAE reviews it", desc: "A multi-model examiner engine marks against your specific brief and rubric." },
  { icon: BarChart3, title: "Improve, then submit", desc: "See your predicted grade, submission readiness and exactly where to improve." },
];

const FEATURES = [
  { icon: Target, title: "Grade Prediction", desc: "A realistic predicted grade with a transparent confidence score — based on your assessment criteria, not generic quality.", href: "/features/grade-prediction" },
  { icon: GraduationCap, title: "Essay Feedback", desc: "Strengths, weaknesses and a lecturer-style narrative explaining what would earn a higher band.", href: "/features/essay-feedback" },
  { icon: FileText, title: "Assignment Brief Analysis", desc: "Unpack your brief before you write — tasks, learning outcomes, and an implicit rubric when none is provided.", href: "/features/assignment-brief-analysis" },
  { icon: ShieldCheck, title: "Submission Intelligence", desc: "A final safety pass before you submit — similarity, referencing, evidence and (coming soon) AI Provenance.", href: "/features/submission-intelligence" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  useEffect(() => { track("landing_viewed"); }, []);

  const start = () => { track("landing_cta_click"); navigate("/new"); };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="landing-page">
      {/* Top nav */}
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl backdrop-saturate-150 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <MyAELogo size={32} />
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <button onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn"
                className="rounded-lg bg-zinc-100 hover:bg-zinc-200 px-4 py-2 text-sm font-medium text-zinc-900 transition-colors">Dashboard</button>
            ) : (
              <>
                <button onClick={() => setAuthOpen({ open: true, mode: "login" })} data-testid="landing-login"
                  className="rounded-lg px-3 sm:px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors">Log in</button>
                <button onClick={() => setAuthOpen({ open: true, mode: "register" })} data-testid="landing-signup"
                  className="rounded-lg border border-zinc-300 hover:border-zinc-900 px-3 sm:px-4 py-2 text-sm font-medium text-zinc-900 transition-colors">Sign up</button>
              </>
            )}
            <button onClick={start} data-testid="landing-nav-cta"
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 transition-all active:scale-[0.98]">
              Analyse Assignment <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(79,70,229,0.10), transparent 70%)" }} />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 pb-16 sm:pt-28 sm:pb-24 text-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-zinc-200 px-3.5 py-1.5 text-xs font-medium text-zinc-600 shadow-sm mb-6">
              <Sparkles size={13} className="text-indigo-500" /> MyAE — My Academic Edge
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-zinc-900 leading-[1.05]">
              Know your grade<br />before you submit.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-600 max-w-xl mx-auto leading-relaxed">
              Upload your brief, marking criteria and draft. MyAE returns your predicted grade,
              submission readiness and examiner-style feedback — before you hand in.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button onClick={start} data-testid="hero-primary-cta"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 text-base font-medium text-white shadow-[0_8px_24px_rgba(79,70,229,0.35)] hover:bg-indigo-700 transition-all active:scale-[0.98]">
                Analyse Assignment <ArrowRight size={18} />
              </button>
              <button onClick={() => document.getElementById("sample")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-base font-medium text-zinc-900 ring-1 ring-inset ring-zinc-300 shadow-sm hover:bg-zinc-50 transition-all">
                <FileText size={18} /> View Sample Report
              </button>
            </div>
            <p className="mt-5 text-sm font-medium text-emerald-700">Free until further notice — no card required.</p>
          </motion.div>

          {/* Hero preview card */}
          <motion.div id="sample" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-16 mx-auto max-w-3xl rounded-2xl border border-zinc-200 bg-white shadow-[0_12px_40px_rgba(0,0,0,0.08)] overflow-hidden text-left">
            <div className="flex items-center gap-1.5 px-4 h-10 border-b border-zinc-100 bg-zinc-50">
              <span className="w-3 h-3 rounded-full bg-zinc-200" /><span className="w-3 h-3 rounded-full bg-zinc-200" /><span className="w-3 h-3 rounded-full bg-zinc-200" />
              <span className="ml-3 text-xs text-zinc-400 font-medium">MyAE · Examiner Report</span>
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="overline text-zinc-400">Predicted grade</p>
                  <p className="font-display text-6xl font-semibold tracking-tighter text-zinc-900 leading-none mt-1">68<span className="text-3xl text-zinc-400">%</span></p>
                  <p className="text-sm text-zinc-500 mt-1">Upper Second (2:1) · 2% from a First</p>
                </div>
                <div className="rounded-xl bg-indigo-50 px-5 py-4 text-center">
                  <p className="overline text-indigo-400">Confidence</p>
                  <p className="font-display text-3xl font-semibold text-indigo-600">High</p>
                  <p className="text-xs text-indigo-500/80 mt-0.5">84%</p>
                </div>
              </div>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {["Critical analysis", "Referencing", "Structure", "Academic writing"].map((c, i) => (
                  <div key={c} className="rounded-xl border border-zinc-200 p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-700">{c}</span>
                      <span className="font-medium text-zinc-900">{[72, 64, 80, 70][i]}%</span>
                    </div>
                    <div className="h-1.5 bg-zinc-100 rounded-full mt-2 overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${[72, 64, 80, 70][i]}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="text-center mb-12">
          <p className="overline text-indigo-500 mb-2">How it works</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-zinc-900">Three steps to a confident submission</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
              <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center mb-4"><s.icon size={20} className="text-indigo-600" /></div>
              <p className="text-xs font-semibold text-zinc-400 mb-1">STEP {i + 1}</p>
              <h3 className="font-display text-lg font-medium text-zinc-900">{s.title}</h3>
              <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-white border-y border-zinc-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <div className="text-center mb-12">
            <p className="overline text-indigo-500 mb-2">Why MyAE</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-zinc-900">Feedback that feels like a real examiner</h2>
            <p className="mt-3 text-sm text-zinc-500">Each feature is a separate lens on your work. Together they help you submit with confidence.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {FEATURES.map((f) => (
              <Link key={f.title} to={f.href} className="group flex gap-4 rounded-2xl border border-zinc-200 hover:border-indigo-300 bg-white p-6 transition-colors" data-testid={`landing-feature-${f.href.split("/").pop()}`}>
                <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0"><f.icon size={20} className="text-indigo-600" /></div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-medium text-zinc-900 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{f.desc}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-600">Learn more →</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/features" className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:text-indigo-900" data-testid="landing-features-hub-link">
              See every MyAE feature <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <div className="rounded-3xl bg-gradient-to-br from-indigo-600 to-indigo-800 px-8 py-14 text-center shadow-[0_20px_60px_rgba(79,70,229,0.35)]">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">Know your grade before you submit.</h2>
          <p className="mt-4 text-indigo-100 max-w-xl mx-auto">Join students using MyAE — My Academic Edge — to hand in with confidence. Free until further notice.</p>
          <button onClick={start} data-testid="cta-bottom"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-base font-medium text-indigo-700 shadow-sm hover:bg-indigo-50 transition-all active:scale-[0.98]">
            Analyse Assignment <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white" data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-2 sm:grid-cols-4 gap-8">
          <div className="col-span-2 sm:col-span-1">
            <MyAELogo size={26} />
            <p className="mt-3 text-xs text-zinc-500 leading-relaxed">Your AI-assisted study companion for university assignments. Know your grade before you submit.</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Features</p>
            <ul className="space-y-2 text-sm text-zinc-600">
              <li><Link to="/features/grade-prediction" className="hover:text-zinc-900">Grade Prediction</Link></li>
              <li><Link to="/features/assignment-analysis" className="hover:text-zinc-900">Assignment Analysis</Link></li>
              <li><Link to="/features/essay-feedback" className="hover:text-zinc-900">Essay Feedback</Link></li>
              <li><Link to="/features/submission-intelligence" className="hover:text-zinc-900">Submission Intelligence</Link></li>
              <li><Link to="/features/assignment-brief-analysis" className="hover:text-zinc-900">Assignment Brief Analysis</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Product</p>
            <ul className="space-y-2 text-sm text-zinc-600">
              <li><Link to="/features" className="hover:text-zinc-900">All features</Link></li>
              <li><Link to="/pricing" className="hover:text-zinc-900">Pricing</Link></li>
              <li><Link to="/contact" className="hover:text-zinc-900">Contact</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Legal</p>
            <ul className="space-y-2 text-sm text-zinc-600">
              <li><Link to="/privacy" className="hover:text-zinc-900">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-zinc-900">Terms of Service</Link></li>
              <li><Link to="/cookies" className="hover:text-zinc-900">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-zinc-100">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 text-xs text-zinc-500 leading-relaxed flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="site-footer-legal">
            <div className="text-center sm:text-left">
              <p>MyAE is a trading name of OpenDegree Ltd.</p>
              <p>OpenDegree Ltd is registered in England and Wales (Company No. 17397971).</p>
              <p className="text-zinc-400 mt-1">© {new Date().getFullYear()} OpenDegree Ltd. All rights reserved.</p>
            </div>
            <SocialLinks />
          </div>
        </div>
      </footer>
    </div>
  );
}
